import { useState } from "react"
import { DownOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import { useShallow } from "zustand/react/shallow"
import {
  AnalyticsAction,
  useAnalyticsEvent,
  PointCostText,
  ResourceUploadField,
  Text,
  type ResourceUrlsChangeReason,
} from "~/components"
import { getVideoAnalysisPointCost } from "~/common/billing/pointCosts"
import { VIDEO_TOOL_LABELS } from "~/common/constants"
import {
  RECREATE_IMAGE_LIMIT,
  RECREATE_VIDEO_RANGE,
} from "~/common/video/recreate"
import { IMAGE_UPLOAD_REQUIREMENTS } from "~/common/media/uploadedResources"
import { linesToList } from "~/common/utils"
import { useHotVideoStore } from "~/store/hotVideo"
import { AdvancedSettings } from "./AdvancedSettings"
import { ProductInfo } from "./AdvancedSettings/ProductInfo"
import { ReferenceVideoField } from "./ReferenceVideoField"
import { SectionTitle } from "./SectionTitle"
import { useExpandedSectionScroll } from "./useExpandedSectionScroll"
import styles from "./index.css"

export const InputPanel = ({ staff = false }: { staff?: boolean }) => {
  const sendAnalytics = useAnalyticsEvent({
    category: "爆款复刻页",
    prefix: "爆款复刻",
  })
  const [expanded, setExpanded] = useState(staff)
  const advancedSectionRef = useExpandedSectionScroll(expanded)
  const [validate, setValidate] = useState(false)
  const {
    hasReplicaPrompt,
    isRequesting,
    isAnalyzingProduct,
    isRestoringTaskDraft,
    resourcesPending,
    analyzeAndOptimizePrompt,
    reconcileImageMentions,
    setVideoUrl,
    videoUrl,
    videoImageUrls,
    setVideoImageUrls,
    start_time,
    end_time,
    draftRevision,
  } = useHotVideoStore(
    useShallow((state) => ({
      hasReplicaPrompt: Boolean(state.replicaPrompt.trim()),
      isRequesting: state.isRequesting,
      isAnalyzingProduct: state.isAnalyzingProduct,
      isRestoringTaskDraft: state.isRestoringTaskDraft,
      resourcesPending: state.resourcesPending,
      analyzeAndOptimizePrompt: state.analyzeAndOptimizePrompt,
      reconcileImageMentions: state.reconcileImageMentions,
      setVideoUrl: state.setVideoUrl,
      videoUrl: state.videoUrl,
      videoImageUrls: state.videoImageUrls,
      setVideoImageUrls: state.setVideoImageUrls,
      start_time: state.start_time,
      end_time: state.end_time,
      draftRevision: state.draftRevision,
    })),
  )
  const onParseClick = () =>
    analyzeAndOptimizePrompt({
      onSubmitted: sendAnalytics,
    })
  const onSourceVideoChange = (url: string) => {
    const changed = url.trim() !== videoUrl.trim()
    setVideoUrl(url)
    if (changed && url.trim()) sendAnalytics("上传爆款视频")
  }
  const onProductImagesChange = (
    previousUrls: string[],
    nextUrls: string[],
    reason: ResourceUrlsChangeReason,
  ) => {
    if (
      (reason === "add" || reason === "select") &&
      nextUrls.some((url) => !previousUrls.includes(url))
    ) {
      sendAnalytics("上传商品图")
    }
    reconcileImageMentions(previousUrls, nextUrls, reason === "replace")
  }
  return (
    <Flex
      className={styles.inputPanel}
      component="aside"
      vertical
    >
      <Flex
        className={styles.inputHeader}
        vertical
        gap={6}
      >
        <Typography.Title level={4}>
          {staff ? "融合脚本生成视频" : VIDEO_TOOL_LABELS.recreate}
        </Typography.Title>
        <Text type="secondary">
          {staff
            ? "已载入热门素材、商品参考图与融合脚本，审核或编辑脚本后即可创建视频"
            : "输入参考视频与新参考图，AI 自动生成同款爆款脚本；人工审核确认后，即可一键生成视频"}
        </Text>
      </Flex>
      <Flex
        vertical
        className={styles.inputScroll}
        gap={24}
      >
        <fieldset
          className={styles.fields}
          disabled={isRequesting || isRestoringTaskDraft}
        >
          <Flex
            vertical
            gap={32}
          >
            <ReferenceVideoField
              allowClip={!staff}
              className={
                validate && !videoUrl.trim()
                  ? styles.requiredFieldError
                  : undefined
              }
              onChange={onSourceVideoChange}
            />
            <ResourceUploadField
              label={
                <SectionTitle
                  number={2}
                  title="新参考图"
                />
              }
              required
              subtitle="上传商品白底图、场景图。若无白底图，可先上传常规商品图，点击图片左上角图标一键生成白底图"
              className={
                validate && !linesToList(videoImageUrls).length
                  ? styles.requiredFieldError
                  : undefined
              }
              value={videoImageUrls}
              onChange={setVideoImageUrls}
              resourceType="image"
              multiple
              sortable
              maxCount={RECREATE_IMAGE_LIMIT}
              rejectOverflow
              requirementsText={IMAGE_UPLOAD_REQUIREMENTS}
              onUrlsChange={onProductImagesChange}
            />
            <Flex
              ref={advancedSectionRef}
              vertical
              gap={16}
            >
              <AnalyticsAction
                action={() => !expanded && "展开高级设置"}
                prefix="爆款复刻"
              >
                <button
                  className={styles.advancedToggle}
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setExpanded((value) => !value)}
                >
                  <Flex
                    component="span"
                    align="center"
                    gap={16}
                  >
                    <SectionTitle
                      number={3}
                      title="高级配置"
                      optional
                    />
                    <span className={styles.toggleIcon}>
                      <DownOutlined rotate={expanded ? 180 : 0} />
                    </span>
                  </Flex>
                </button>
              </AnalyticsAction>
              <div hidden={!expanded}>
                {staff ? (
                  <ProductInfo key={draftRevision} />
                ) : (
                  <AdvancedSettings key={draftRevision} />
                )}
              </div>
            </Flex>
          </Flex>
        </fieldset>
      </Flex>
      <Flex
        className={styles.parseActionBar}
        align="center"
        justify="center"
        wrap
        gap={12}
      >
        {staff ? (
          <Text type="secondary">
            可直接编辑右侧脚本；需要重新融合时，请返回融合提示词页面。
          </Text>
        ) : (
          <>
            <AnalyticsAction
              action={
                hasReplicaPrompt ? "点击重新生成提示词" : "点击生成新视频提示词"
              }
              prefix="爆款复刻"
            >
              <Button
                disabled={
                  isRequesting ||
                  isRestoringTaskDraft ||
                  resourcesPending ||
                  isAnalyzingProduct
                }
                loading={isRequesting}
                type="primary"
                className={styles.parseButton}
                onClick={() => {
                  setValidate(true)
                  onParseClick()
                }}
              >
                {hasReplicaPrompt ? "AI 重新生成视频脚本" : "AI 生成视频脚本"}
              </Button>
            </AnalyticsAction>
            <PointCostText
              cost={getVideoAnalysisPointCost(
                end_time > start_time
                  ? Math.min(end_time - start_time, RECREATE_VIDEO_RANGE.MAX)
                  : undefined,
              )}
            />
          </>
        )}
      </Flex>
    </Flex>
  )
}
